/**
 * Replies API - Read Operations
 *
 * Fetching replies for comments.
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  getDocsFromCache,
  getDocsFromServer,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { ensureFreshToken } from "../authUtils";
import { getFirestoreFetchPolicy, isLowQuotaMode } from "../firestoreFetchPolicy";
import {
  normalizeTimestamp,
  normalizeUserData,
  normalizeContentField,
} from "../commentsApi";

/**
 * Fetches all replies for a comment, ordered oldest-first (chronological thread).
 * Respects the configured Firestore fetch policy.
 *
 * Soft-deleted replies (deletedAt != null) are intentionally included so that
 * reply threads retain their structural continuity.  The UI renders
 * "[Reply removed]" for those entries.
 *
 * @param {string} commentId
 * @param {{ fetchPolicy?: string, preferServer?: boolean }} [opts]
 * @returns {Promise<Array>}
 */
export const getCommentReplies = async (
  commentId,
  { fetchPolicy: inputPolicy, preferServer } = {},
) => {
  try {
    await ensureFreshToken();

    const repliesRef = collection(db, "replies");
    const repliesQuery = query(
      repliesRef,
      where("commentId", "==", commentId),
      orderBy("timestamp", "asc"),
    );

    const lowQuota = isLowQuotaMode();
    const fetchPolicy = getFirestoreFetchPolicy(inputPolicy);

    let repliesSnapshot;

    // ── Cache-first / cache-only path ──
    if (fetchPolicy === "cache-only" || fetchPolicy === "cache-first") {
      try {
        const cachedSnap = await (typeof getDocsFromCache === "function"
          ? getDocsFromCache(repliesQuery)
          : getDocs(repliesQuery));
        if (cachedSnap && (!cachedSnap.empty || fetchPolicy === "cache-only")) {
          repliesSnapshot = cachedSnap;
        } else if (lowQuota || preferServer === false) {
          repliesSnapshot = cachedSnap; // may be empty
        }
      } catch (_) {
        // Cache miss → fall through to server
      }
    }

    // ── Network path ──
    if (!repliesSnapshot && fetchPolicy !== "cache-only") {
      try {
        repliesSnapshot = await (typeof getDocsFromServer === "function"
          ? getDocsFromServer(repliesQuery)
          : getDocs(repliesQuery));
      } catch {
        repliesSnapshot = await getDocs(repliesQuery);
      }
    }

    const replies = [];
    repliesSnapshot.forEach((d) => {
      const data = d.data();
      const normalizedTimestamp = normalizeTimestamp(data.timestamp);
      const normalizedUpdatedAt = data.updatedAt
        ? normalizeTimestamp(data.updatedAt)
        : null;
      const userData = normalizeUserData(data);

      replies.push({
        id: d.id,
        commentId: data.commentId || null, // CRITICAL: Ensure commentId is preserved for hydration
        userId: data.userId || null,
        parentReplyId: data.parentReplyId || null,
        userProfession: userData.profession || "",
        profession: userData.profession || "",
        username: userData.username,
        photoURL: userData.photoURL || "",
        text: normalizeContentField(data),
        timestamp: normalizedTimestamp,
        likes: data.likes || 0,
        // Soft-delete fields
        deletedAt: data.deletedAt ? normalizeTimestamp(data.deletedAt) : null,
        updatedAt: normalizedUpdatedAt,
        isEdited: !!normalizedUpdatedAt,
      });
    });

    return replies;
  } catch (error) {
    console.error("Error getting comment replies:", error);
    throw error;
  }
};
